import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ispPackageAPI, walletAPI } from '../services/api';

// ── Icons ─────────────────────────────────────────────────────────────────────
const Wifi = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12.55a11 11 0 0 1 14.08 0"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/>
    <path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><line x1="12" y1="20" x2="12.01" y2="20"/>
  </svg>
);
const Copy = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
  </svg>
);
const Check = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const Clock = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
  </svg>
);
const ChevronDown = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = n => Number(n || 0).toLocaleString();

function statusBadge(status) {
  const map = {
    completed: { bg: '#dcfce7', color: '#166534', label: 'Delivered' },
    pending:   { bg: '#fef9c3', color: '#854d0e', label: 'Pending'   },
    failed:    { bg: '#fee2e2', color: '#991b1b', label: 'Failed'    },
    refunded:  { bg: '#e0e7ff', color: '#3730a3', label: 'Refunded'  },
  };
  const s = map[status] || { bg: '#f3f4f6', color: '#374151', label: status };
  return (
    <span style={{ background: s.bg, color: s.color, padding: '2px 10px', borderRadius: 99,
      fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
      {s.label}
    </span>
  );
}

// manual_activation lifecycle badge — Activating / Active until {date} / Expired
function activationBadge(providerStatus, expiresAt) {
  const map = {
    awaiting_activation: { bg: '#fef9c3', color: '#854d0e', label: 'Activating' },
    active:   { bg: '#dcfce7', color: '#166534',
                label: expiresAt ? 'Active until ' + new Date(expiresAt).toLocaleDateString() : 'Active' },
    expired:  { bg: '#fee2e2', color: '#991b1b', label: 'Expired' },
  };
  const s = map[providerStatus] || { bg: '#f3f4f6', color: '#374151', label: providerStatus };
  return (
    <span style={{ background: s.bg, color: s.color, padding: '2px 10px', borderRadius: 99,
      fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
      {s.label}
    </span>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function InternetPackages() {
  const [tab, setTab]           = useState('packages');     // 'packages' | 'history'
  const [packages, setPackages] = useState([]);
  const [history, setHistory]   = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [histPage, setHistPage] = useState(1);
  const [imports, setImports]   = useState([]);      // linked pre-app subscriptions
  const [balance, setBalance]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [result, setResult]     = useState(null);   // { credential, purchase }
  const [copied, setCopied]     = useState(false);
  const [copiedField, setCopiedField] = useState(null); // 'username' | 'password' | 'both' | null
  const [err, setErr]           = useState('');
  const [renewingId, setRenewingId]   = useState(null);

  // ── Load ─────────────────────────────────────────────────────────────────────
  const loadPackages = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const [pkgRes, walRes] = await Promise.all([
        ispPackageAPI.list(),
        walletAPI.portfolio().catch(() => null),
      ]);
      setPackages(pkgRes.packages || []);
      if (walRes) {
        const ugx = (walRes.portfolio?.wallets || []).find(w => w.currency === 'UGX' && w.walletType === 'primary')
                 || (walRes.portfolio?.wallets || []).find(w => w.currency === 'UGX');
        setBalance(ugx ? ugx.balance : null);
      }
    } catch (e) {
      setErr(e.response?.data?.message || e.message || 'Failed to load packages');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async (page = 1) => {
    try {
      const r = await ispPackageAPI.myPurchases(page, 10);
      setHistory(r.docs || []);
      setHistoryTotal(r.total || 0);
      setHistPage(page);
      setImports(r.imports || []);
    } catch (e) {
      setErr(e.response?.data?.message || e.message);
    }
  }, []);

  useEffect(() => { loadPackages(); }, [loadPackages]);
  useEffect(() => { if (tab === 'history') loadHistory(1); }, [tab, loadHistory]);

  // Renew — buy the same package again (manual_activation renewal flow)
  const handleRenew = async (packageIdOrObj) => {
    const pid = typeof packageIdOrObj === 'object' ? packageIdOrObj?._id : packageIdOrObj;
    if (!pid) return;
    setRenewingId(pid);
    setErr('');
    try {
      const r = await ispPackageAPI.purchase(pid, undefined, undefined);
      setResult({ credential: r.credential, purchase: r.purchase });
      await loadHistory(1);
      await loadPackages();
    } catch (e) {
      setErr(e.response?.data?.message || e.message || 'Renewal failed');
    } finally {
      setRenewingId(null);
    }
  };

  const copyCredential = () => {
    if (!result?.credential) return;
    navigator.clipboard.writeText(result.credential).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const copyField = (fieldKey, text) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(fieldKey);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  // ── Styles ────────────────────────────────────────────────────────────────────
  const styles = {
    page:      { maxWidth: 600, margin: '0 auto', padding: '0 0 80px' },
    hero:      { background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)',
                 padding: '28px 20px 32px', color: '#fff' },
    heroTitle: { fontSize: 22, fontWeight: 700, margin: 0 },
    heroSub:   { fontSize: 13, color: 'rgba(255,255,255,0.65)', marginTop: 4 },
    balance:   { marginTop: 16, background: 'rgba(255,255,255,0.08)', borderRadius: 10,
                 padding: '10px 14px', display: 'inline-block' },
    balLbl:    { fontSize: 11, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.05em', textTransform: 'uppercase' },
    balAmt:    { fontSize: 20, fontWeight: 700, color: '#22d3ee', marginTop: 2 },
    tabs:      { display: 'flex', borderBottom: '1px solid var(--border)', background: 'var(--bg-card)' },
    tab:       (active) => ({
                 flex: 1, padding: '12px 0', textAlign: 'center', fontSize: 14, fontWeight: 600,
                 cursor: 'pointer', border: 'none', background: 'none',
                 color: active ? '#22d3ee' : 'var(--text-muted)',
                 borderBottom: active ? '2px solid #22d3ee' : '2px solid transparent',
               }),
    grid:      { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                 gap: 12, padding: '16px 12px' },
    card:      (inStock) => ({
                 background: 'var(--bg-card)', borderRadius: 12, overflow: 'hidden',
                 border: '1px solid var(--border)', opacity: inStock ? 1 : 0.55,
                 transition: 'box-shadow 0.15s', cursor: inStock ? 'pointer' : 'default',
               }),
    cardTop:   { background: 'linear-gradient(135deg, #0f172a, #1e3a5f)', padding: '14px 16px' },
    cardName:  { fontSize: 15, fontWeight: 700, color: '#fff', margin: 0 },
    cardDur:   { fontSize: 12, color: 'rgba(255,255,255,0.6)', marginTop: 3 },
    cardBody:  { padding: '12px 16px' },
    price:     { fontSize: 22, fontWeight: 800, color: 'var(--text-primary)' },
    priceSub:  { fontSize: 11, color: 'var(--text-muted)', marginLeft: 2 },
    buyBtn:    (disabled) => ({
                 marginTop: 12, width: '100%', padding: '10px 0', borderRadius: 8,
                 background: disabled ? '#64748b' : '#22d3ee',
                 color: disabled ? '#94a3b8' : '#0f172a',
                 fontWeight: 700, fontSize: 14, border: 'none',
                 cursor: disabled ? 'not-allowed' : 'pointer',
               }),
    badge:     { display: 'inline-block', background: '#0ea5e9', color: '#fff',
                 fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                 letterSpacing: '0.05em', marginBottom: 4 },
    importedBadge: { display: 'inline-block', background: '#a855f7', color: '#fff',
                 fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                 letterSpacing: '0.04em', marginLeft: 6 },
    renewBtn:  (disabled) => ({
                 marginTop: 10, padding: '7px 16px', borderRadius: 8,
                 background: disabled ? '#64748b' : '#22d3ee',
                 color: disabled ? '#94a3b8' : '#0f172a',
                 fontWeight: 700, fontSize: 12, border: 'none',
                 cursor: disabled ? 'not-allowed' : 'pointer',
               }),
    lowStock:  { display: 'inline-block', background: '#fef3c7', color: '#92400e',
                 fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                 letterSpacing: '0.04em', marginLeft: 4 },
    outStock:  { display: 'inline-block', background: '#fee2e2', color: '#991b1b',
                 fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                 letterSpacing: '0.04em', marginBottom: 4 },
    speed:     { fontSize: 12, color: 'var(--text-muted)', marginTop: 4 },
    cancelLink: { display: 'block', textAlign: 'center', marginTop: 12,
                  color: 'var(--text-muted)', fontSize: 13, cursor: 'pointer', border: 'none',
                  background: 'none', width: '100%' },
    // Result card
    resultCard: { margin: 12, background: 'var(--bg-card)', borderRadius: 14,
                  border: '2px solid #22d3ee', padding: '20px' },
    resultTtl:  { fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 6px' },
    credBox:    { background: 'var(--bg-page)', borderRadius: 8, padding: '12px 14px',
                  fontFamily: 'monospace', fontSize: 20, fontWeight: 700, letterSpacing: '0.1em',
                  color: '#22d3ee', textAlign: 'center', margin: '12px 0', wordBreak: 'break-all' },
    copyBtn:    { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  width: '100%', padding: '10px 0', borderRadius: 8,
                  background: copied ? '#dcfce7' : '#0f172a', color: copied ? '#166534' : '#22d3ee',
                  fontWeight: 700, fontSize: 14, border: '1px solid #22d3ee', cursor: 'pointer' },
    redemption: { fontSize: 12, color: 'var(--text-muted)', marginTop: 10, lineHeight: 1.5 },
    // History
    histList:  { padding: '12px' },
    histItem:  { background: 'var(--bg-card)', borderRadius: 10, padding: '14px',
                 marginBottom: 10, border: '1px solid var(--border)' },
    histName:  { fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' },
    histMeta:  { fontSize: 12, color: 'var(--text-muted)', marginTop: 2, display: 'flex',
                 alignItems: 'center', gap: 8, flexWrap: 'wrap' },
    histCred:  { fontFamily: 'monospace', background: 'var(--bg-page)', borderRadius: 6,
                 padding: '4px 8px', fontSize: 13, color: '#22d3ee', letterSpacing: '0.06em',
                 display: 'inline-block', marginTop: 8 },
    errBox:    { background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 8,
                 margin: '8px 12px', fontSize: 13 },
    emptyMsg:  { textAlign: 'center', color: 'var(--text-muted)', padding: '40px 20px', fontSize: 14 },
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div style={styles.page}>
      {/* Hero */}
      <div style={styles.hero}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Wifi />
          <h1 style={styles.heroTitle}>Internet Packages</h1>
        </div>
        <p style={styles.heroSub}>Buy Wi-Fi vouchers instantly — credited to your DWIPay wallet</p>
        {balance !== null && (
          <div style={styles.balance}>
            <div style={styles.balLbl}>Available Balance</div>
            <div style={styles.balAmt}>UGX {fmt(balance)}</div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        <button style={styles.tab(tab === 'packages')} onClick={() => setTab('packages')}>
          Buy Packages
        </button>
        <button style={styles.tab(tab === 'history')} onClick={() => setTab('history')}>
          My Purchases
        </button>
      </div>

      {/* Error */}
      {err && <div style={styles.errBox}>{err}</div>}

      {/* Credential Result */}
      {result && result.purchase?.providerStatus === 'awaiting_activation' && !result.credential && (
        <div style={styles.resultCard}>
          <p style={styles.resultTtl}>Payment Received</p>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 4px' }}>
            {result.purchase?.packageSnapshot?.name}
          </p>
          <p style={{ fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.5, margin: '12px 0' }}>
            Your package is being activated (usually under 5 minutes). You'll get a notification
            the moment it's live — just connect to DWIFI as usual.
          </p>
          <button
            style={{ ...styles.cancelLink, marginTop: 4, color: 'var(--text-primary)', fontWeight: 600 }}
            onClick={() => setResult(null)}
          >
            Continue
          </button>
        </div>
      )}
      {result && result.purchase?.radiusUsername && (
        // BUG FIX (2026-09-14): a hotspot package used to deliver a voucher
        // code here and tell the customer to go "redeem" it at the captive
        // portal — a step that always failed with "already used" because the
        // code was finalized the instant this purchase completed (see
        // services/isp/ispPackageService.js's useDirectRadiusActivation).
        // Access is now granted directly at purchase time, so this shows the
        // real, working credential — no redeem step, nothing left to fail.
        <div style={styles.resultCard}>
          <p style={styles.resultTtl}>✅ Payment Successful!</p>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 4px' }}>
            {result.purchase?.packageSnapshot?.name}
          </p>
          <p style={{ fontSize: 14, color: 'var(--text-primary)', margin: '0 0 12px', fontWeight: 600 }}>
            To connect, enter these details on the WiFi login page:
          </p>
          <div style={{ ...styles.credBox, display: 'flex', alignItems: 'center', justifyContent: 'space-between', textAlign: 'left', fontSize: 15 }}>
            <span>Username: <strong>{result.purchase.radiusUsername}</strong></span>
            <button
              style={{ background: copiedField === 'username' ? '#dcfce7' : '#0f172a', color: copiedField === 'username' ? '#166534' : '#22d3ee', border: '1px solid #22d3ee', borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0, marginLeft: 8 }}
              onClick={() => copyField('username', result.purchase.radiusUsername)}
            >
              {copiedField === 'username' ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <div style={{ ...styles.credBox, display: 'flex', alignItems: 'center', justifyContent: 'space-between', textAlign: 'left', fontSize: 15 }}>
            <span>Password: <strong>{result.purchase.radiusPassword}</strong></span>
            <button
              style={{ background: copiedField === 'password' ? '#dcfce7' : '#0f172a', color: copiedField === 'password' ? '#166534' : '#22d3ee', border: '1px solid #22d3ee', borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0, marginLeft: 8 }}
              onClick={() => copyField('password', result.purchase.radiusPassword)}
            >
              {copiedField === 'password' ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <button
            style={styles.copyBtn}
            onClick={() => copyField('both', `Username: ${result.purchase.radiusUsername}\nPassword: ${result.purchase.radiusPassword}`)}
          >
            {copiedField === 'both' ? <Check /> : <Copy />}
            {copiedField === 'both' ? 'Copied!' : 'Copy Both'}
          </button>
          <p style={styles.redemption}>
            <strong>How to use:</strong><br/>
            Connect to DWIFI WiFi. If a login page appears, enter this username and password to get online.
          </p>
          <button
            style={{ ...styles.cancelLink, marginTop: 16, color: 'var(--text-primary)', fontWeight: 600 }}
            onClick={() => setResult(null)}
          >
            Continue
          </button>
        </div>
      )}
      {result && result.credential && !result.purchase?.radiusUsername && (
        <div style={styles.resultCard}>
          <p style={styles.resultTtl}>Your Internet Voucher</p>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 4px' }}>
            {result.purchase?.packageSnapshot?.name}
          </p>
          <div style={styles.credBox}>{result.credential}</div>
          <button style={styles.copyBtn} onClick={copyCredential}>
            {copied ? <Check /> : <Copy />}
            {copied ? 'Copied!' : 'Copy Code'}
          </button>
          {result.purchase?.packageSnapshot?.redemptionInstructions && (
            <p style={styles.redemption}>
              <strong>How to use:</strong><br/>
              {result.purchase.packageSnapshot.redemptionInstructions}
            </p>
          )}
          <button
            style={{ ...styles.cancelLink, marginTop: 16, color: 'var(--text-primary)', fontWeight: 600 }}
            onClick={() => setResult(null)}
          >
            Continue
          </button>
        </div>
      )}

      {/* Have an existing package? */}
      {tab === 'packages' && !result && (
        <div style={{ margin: '12px 12px 0', textAlign: 'center' }}>
          <Link
            to="/link-package"
            style={{ color: '#22d3ee', fontSize: 13, fontWeight: 600, textDecoration: 'none', display: 'inline-block', padding: '6px 0' }}
          >
            Already a DWIFI customer? Link your existing package →
          </Link>
        </div>
      )}

      {/* Packages Tab */}
      {tab === 'packages' && !result && (
        loading ? (
          <div style={styles.emptyMsg}>Loading packages…</div>
        ) : packages.length === 0 ? (
          <div style={styles.emptyMsg}>
            <Wifi />
            <p>No internet packages available right now.<br/>Check back soon or contact support.</p>
          </div>
        ) : (
          <div style={styles.grid}>
            {packages.map(pkg => (
              <Link key={pkg._id} to={pkg.inStock ? `/buy-package/${pkg._id}` : '#'}
                style={{ ...styles.card(pkg.inStock), textDecoration: 'none', display: 'block' }}
                onClick={e => !pkg.inStock && e.preventDefault()}>
                <div style={styles.cardTop}>
                  {pkg.badge && <div style={styles.badge}>{pkg.badge}</div>}
                  {pkg.lowStock && <div style={styles.lowStock}>LOW STOCK</div>}
                  {!pkg.inStock && <div style={styles.outStock}>OUT OF STOCK</div>}
                  <p style={styles.cardName}>{pkg.name}</p>
                  <p style={styles.cardDur}>
                    <Clock /> &nbsp;{pkg.durationLabel}
                  </p>
                </div>
                <div style={styles.cardBody}>
                  {pkg.speedInfo && <p style={styles.speed}>{pkg.speedInfo}</p>}
                  <div style={{ marginTop: 6 }}>
                    <span style={styles.price}>UGX {fmt(pkg.priceUGX)}</span>
                    <span style={styles.priceSub}>/package</span>
                  </div>
                  {pkg.description && (
                    <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.4 }}>
                      {pkg.description}
                    </p>
                  )}
                  <span style={{ ...styles.buyBtn(!pkg.inStock), display: 'block', textAlign: 'center', boxSizing: 'border-box' }}>
                    {pkg.inStock ? 'Buy Now' : 'Out of Stock'}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )
      )}

      {/* History Tab */}
      {tab === 'history' && !result && (
        <div style={styles.histList}>
          {history.length === 0 && imports.length === 0 ? (
            <div style={styles.emptyMsg}>
              <p>No purchases yet.<br/>Buy a package and it will appear here.</p>
            </div>
          ) : (
            <>
              {history.map(p => {
                const isManualActivation = p.packageSnapshot?.providerId === 'manual_activation';
                const pkgId = p.packageId?._id || p.packageId;
                return (
                <div key={p._id || p.purchaseId} style={styles.histItem}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <span style={styles.histName}>
                      {p.packageSnapshot?.name || p.packageId?.name || 'Internet Package'}
                    </span>
                    {isManualActivation ? activationBadge(p.providerStatus, p.expiresAt) : statusBadge(p.paymentStatus)}
                  </div>
                  <div style={styles.histMeta}>
                    <span>UGX {fmt(p.amountUGX)}</span>
                    <span>·</span>
                    <span>{p.packageSnapshot?.durationLabel}</span>
                    <span>·</span>
                    <span>{new Date(p.createdAt).toLocaleDateString()}</span>
                  </div>
                  {!isManualActivation && p.credential && p.paymentStatus === 'completed' && (
                    <div>
                      <span style={styles.histCred}>{p.credential}</span>
                      <button
                        style={{ background: 'none', border: 'none', cursor: 'pointer',
                          color: 'var(--text-muted)', marginLeft: 6, verticalAlign: 'middle' }}
                        onClick={() => navigator.clipboard.writeText(p.credential).catch(() => {})}
                        title="Copy code"
                      >
                        <Copy />
                      </button>
                    </div>
                  )}
                  {!isManualActivation && p.packageSnapshot?.redemptionInstructions && p.paymentStatus === 'completed' && (
                    <p style={{ ...styles.redemption, color: 'var(--text-muted)' }}>
                      {p.packageSnapshot.redemptionInstructions}
                    </p>
                  )}
                  {isManualActivation && (p.providerStatus === 'active' || p.providerStatus === 'expired') && pkgId && (
                    <button
                      style={styles.renewBtn(renewingId === pkgId)}
                      disabled={renewingId === pkgId}
                      onClick={() => handleRenew(pkgId)}
                    >
                      {renewingId === pkgId ? 'Renewing…' : 'Renew'}
                    </button>
                  )}
                </div>
                );
              })}
              {historyTotal > history.length && (
                <button
                  style={{ display: 'block', margin: '0 auto', padding: '8px 24px',
                    borderRadius: 8, border: '1px solid var(--border)', background: 'none',
                    color: 'var(--text-muted)', cursor: 'pointer', fontSize: 13 }}
                  onClick={() => loadHistory(histPage + 1)}
                >
                  Load More
                </button>
              )}
              {imports.map(s => {
                const pkgId = s.packageId?._id || s.packageId;
                return (
                <div key={s._id} style={styles.histItem}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <span style={styles.histName}>
                      {s.packageId?.name || s.packageNameSnapshot || 'Internet Package'}
                      <span style={styles.importedBadge}>IMPORTED</span>
                    </span>
                    {activationBadge(s.status === 'active' ? 'active' : 'expired', s.expiresAt)}
                  </div>
                  <div style={styles.histMeta}>
                    <span>{s.status === 'active' ? 'Active until' : 'Expired'} {new Date(s.expiresAt).toLocaleDateString()}</span>
                  </div>
                  {pkgId && (
                    <button
                      style={styles.renewBtn(renewingId === pkgId)}
                      disabled={renewingId === pkgId}
                      onClick={() => handleRenew(pkgId)}
                    >
                      {renewingId === pkgId ? 'Renewing…' : 'Renew'}
                    </button>
                  )}
                </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}
